/**
 * Single RTK Query baseApi.
 *
 * Per `tnn-engineering-conventions.md` §16.1, every feature injects endpoints
 * into this one base API rather than creating its own. The store only mounts
 * `baseApi.reducer`; features add endpoints via `baseApi.injectEndpoints(...)`.
 *
 * Tag types are added here as features are wired in. When you add a new
 * feature, add its tag to the `tagTypes` array and the feature's API file
 * will reference it via `providesTags` / `invalidatesTags`.
 */
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { apiV1Base } from "@/shared/lib/authorizedFetch";
import { currentAppPath, stashReturnPath } from "@/features/auth/lib/sessionReturnPath";
import { BARE_LOGIN_PATH, resolveRequestTenantSlug } from "@/shared/lib/tenantSlug";

import type { RootState } from "../index";
import { tokenRefreshed, logout } from "../slices/authSlice";


function redirectToLoginAfterSessionLoss(): void {
  if (typeof window === "undefined") return;
  stashReturnPath(currentAppPath());
  window.location.href = BARE_LOGIN_PATH;
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: apiV1Base(),
  prepareHeaders: (headers, { getState }) => {
      // Attach tenant slug + JWT to every request.
      //
      // Tenant slug for X-Tenant-Slug - see resolveRequestTenantSlug().
      const state = getState() as RootState;
      const slug = resolveRequestTenantSlug({
        accessToken: state.auth?.accessToken ?? null,
        user: state.auth?.user ?? null,
        tenantSlug: state.auth?.tenantSlug ?? null,
      });
      // Always send when a slug resolves (URL /o/<slug>/, subdomain, session, or env).
      if (slug) {
        headers.set("X-Tenant-Slug", slug);
      }

      const token = state.auth?.accessToken;
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }

      return headers;
    },
    // Unwrap the {success, data} envelope so feature endpoints get the
    // raw data straight. Errors flow through the standard RTK Query error
    // channel.
    responseHandler: async (response) => {
      const text = await response.text();
      if (!text) return null;
      try {
        const json = JSON.parse(text);
        if (json && typeof json === "object" && "success" in json && json.success === true) {
          return json.data;
        }
        return json; // error envelope; RTK Query handles via isError
      } catch {
        return text;
      }
    },
});

/**
 * True for the backend's "no tenant slug in request" refusal.
 *
 * Observed on staging in August 2026: the *same* request, with the same
 * X-Tenant-Slug header, succeeds or fails at random - four failures in five
 * identical calls in one sample. The tenant context is being lost somewhere
 * server-side (a worker that resolves it inconsistently), not by the client:
 * this app sets the header from the URL, which cannot vary between two
 * requests a millisecond apart.
 *
 * Until that is fixed at the source, a single retry turns a screen that
 * randomly shows "No tenant slug in request" into one that works. It is a
 * plaster, and deliberately a narrow one: only this error code, only once,
 * so a genuinely mis-addressed request still surfaces.
 */
function isTenantContextFlake(error: FetchBaseQueryError | undefined): boolean {
  if (!error || error.status !== 400) return false;
  const data = error.data as { code?: string } | undefined;
  return data?.code === "tnn.tenancy.slug_missing";
}

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> =
  async (args, api, extraOptions) => {
    const requestUrl = typeof args === "string" ? args : args.url;
    let result = await rawBaseQuery(args, api, extraOptions);

    // Staging drops tenant context at random on some inventory reads
    // (the movement report is the one that surfaces it). One retry is
    // not enough when most of a short burst fails.
    for (let attempt = 0; attempt < 3 && isTenantContextFlake(result.error); attempt += 1) {
      result = await rawBaseQuery(args, api, extraOptions);
    }

    if (
      result.error?.status === 401 &&
      requestUrl !== "/auth/refresh/" &&
      requestUrl !== "/auth/login/"
    ) {
      const state = api.getState() as RootState;
      const refreshToken = state.auth?.refreshToken;

      if (refreshToken) {
        const refreshResult = await rawBaseQuery(
          {
            url: "/auth/refresh/",
            method: "POST",
            body: { refresh: refreshToken },
          },
          api,
          extraOptions,
        );

        if (refreshResult.data) {
          const data = refreshResult.data as { access: string };
          api.dispatch(tokenRefreshed({ access: data.access }));
          result = await rawBaseQuery(args, api, extraOptions);
        } else {
          redirectToLoginAfterSessionLoss();
          api.dispatch(logout());
        }
      } else {
        redirectToLoginAfterSessionLoss();
        api.dispatch(logout());
      }
    }

    return result;
  };

/** The single RTK Query baseApi every feature injects its endpoints into. */
export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  // Tag types - add new types as features land. Tags drive cache invalidation.
  tagTypes: [
    "BankRec",
    "BankTransfer",
    "BillCom",
    "Expense",
    "FundTransfer",
    "RestrictionRelease",
    "JournalApprovals",
    "PostingRule",
    "ReviewQueue",
    "Tenant",
    "VerticalChange",
    "ControlTenant",
    "Manifest",
    "Preferences",
    "PendingCounts",
    "Permissions",
    "MyPermissions",
    "User",
    "Person",
    "Donor",
    "Organization",
    "PersonRole",
    "Timesheet",
    "OrgRole",
    "Relationship",
    "Interaction",
    // Application wizard + admin tracking (5/18/26)
    "Application",
    "WizardState",
    // AI Copilot (5/18/26)
    "Conversation",
    "Playbook",
    // Onboarding walkthrough (5/19/26)
    "OnboardingProgress",
    // Announcements (5/19/26)
    "Announcement",
    "OrgAnnouncement",
    "AdminAnnouncement",
    // Billing (5/19/26)
    "Invoice",
    "AdminInvoice",
    "BillingProfile",
    "BillingCohortSummary",
    // KPI dashboard / ops (5/23/26)
    "KPI",
    "KPIDashboard",
    "MyKPIs",
    // Volunteer hours (5/24/26)
    "VolunteerShift",
    "VolunteerProgram",
    // Accounting - ledger + contributions (5/23/26)
    "Fund",
    "Campaign",
    "Account",
    "Journal",
    "Bill",
    "AccountingDashboard",
    "FiscalYear",
    "FiscalPeriod",
    "Contribution",
    "Pledge",
    "SoftCredit",
    // Org-member invitations (5/24/26)
    "Invitation",
    // QuickBooks (5/24/26)
    "QBO",
    "Payments",
    "EmailConnection",
    // Scheduling - internal + external (5/25/26)
    "Resource",
    "ScheduleTemplate",
    "Shift",
    "Booking",
    "Appointment",
    "SchedEvent",
    "EventMetrics",
    "Attendance",
    "BookingToken",
    // Grants (5/27/26)
    "Grant",
    "GrantPipeline",
    "GrantNote",
    "Funder",
    // Casework - full case management (5/28/26)
    "ServiceCase",
    "AssistanceProgram",
    "CaseDashboard",
    "CaseDocument",
    "DocumentChecklist",
    "CaseReferral",
    "ServiceLog",
    "CaseTask",
    "CaseTaskAttachment",
    "CaseTaskComment",
    "CaseTaskActivity",
    "CaseTimeline",
    "CaseNote",
    "ReferralOrg",
    // Inventory (v4.2.0)
    "Inventory",
    // Vendors - the supplier directory
    "Vendor",
    // Check-In (v4.1.6)
    "CheckIn",
    // Future:
    // "Program", "Project", "KPI",
    // "Shift", "Appointment", "Event", "Resource",
    // "Journal", "JournalLine", "Account", "Fund",
  ],
  endpoints: () => ({}),
});
